import * as React from 'react';

import Knob from 'components/atoms/Knob';
import ButtonBar from 'components/molecules/ButtonBar';
import { ContentImageProps } from 'components/molecules/ContentImage';
import ContentVideo, { ContentVideoProps } from 'components/molecules/ContentVideo';
import ContentImageSlider from 'components/molecules/Slider/ContentImageSlider';
import { ButtonProps } from '../Button/Button';

const MINIMAL_TIME_SPAN_TO_SHOW_TIMER = 480;

export type ExerciseType = {
	title: string;
	images: Array<ContentImageProps>;
	amount: number;
	bodyRegions: Array<string>;
	contentVideo?: ContentVideoProps;
	unit: string;
};

export type CardLayerProps = {
	exercises: Array<ExerciseType>;
	selectedExerciseId: number;
};

export type CardLayerState = {
	currentExerciseId: number;
};

export class CardLayer extends React.Component<CardLayerProps, CardLayerState> {
	public static displayName = 'CardLayer';

	public state: CardLayerState = {
		currentExerciseId: this.props.selectedExerciseId,
	};

	public handleSwitchLayer = (direction: string) => {
		const { exercises } = this.props;
		const currentExerciseId = this.state.currentExerciseId + 1;

		if (direction === 'right') {
			if (currentExerciseId + 1 <= exercises.length) {
				this.setState({ currentExerciseId: this.state.currentExerciseId + 1 });
			}
		} else {
			if (currentExerciseId - 1 > 0) {
				this.setState({ currentExerciseId: this.state.currentExerciseId - 1 });
			}
		}
	};

	public onSwitchLayer = (actionType: string): void => {
		this.handleSwitchLayer(actionType.replace('arrow-', ''));
	};

	public componentDidUpdate(prevProps: CardLayerProps) {
		if (prevProps.selectedExerciseId !== this.props.selectedExerciseId) {
			this.setState({ currentExerciseId: this.props.selectedExerciseId });
		}
	}

	public getCardButtons = () => {
		let prevTitle = '';
		let nextTitle = '';
		let prevType = 'disabled';
		let nextType = 'disabled';

		if (this.state.currentExerciseId !== 0) {
			prevTitle = this.props.exercises[this.state.currentExerciseId - 1].title;
			prevType = '';
		}
		if (this.state.currentExerciseId !== this.props.exercises.length - 1) {
			nextTitle = this.props.exercises[this.state.currentExerciseId + 1].title;
			nextType = '';
		}

		return {
			buttonsLayout: 'side',
			buttons: [
				{
					labelTop: prevTitle,
					modifier: 'wide',
					knob: {
						type: prevType,
						modifier: 'transparent',
						size: 'l',
						icon: 'arrow-left',
						_class: 'js-m-card-button__complete',
					},
				},
				{
					labelTop: nextTitle,
					modifier: 'wide',
					knob: {
						type: nextType,
						modifier: 'transparent',
						size: 'l',
						icon: 'arrow-right',
						_class: '',
					},
				},
			] as ButtonProps[],
		};
	};

	public render() {
		const { title, bodyRegions, images, contentVideo, amount, unit } = this.props.exercises[
			this.state.currentExerciseId
		];
		const cardButtons = this.getCardButtons();

		function calculateSeconds() {
			return amount !== undefined ? amount * 60 : 0;
		}

		function getSeconds() {
			const seconds = unit === 'durationSec' && amount !== undefined ? amount : calculateSeconds();
			return seconds <= MINIMAL_TIME_SPAN_TO_SHOW_TIMER && (unit === 'duration' || unit === 'durationSec')
				? seconds
				: 0;
		}

		return (
			<div className={'m-card-layer'} data-t-name="CardLayer">
				<div className="m-card-layer__wrapper">
					<div className="m-card-layer__back">
						<Knob modifier={'filled'} size={'s'} icon="back" attributes={{ 'data-lity-close': true }} />
					</div>
					<div className="m-card-layer__head">
						<h4 className="m-card-layer__title">{title}</h4>
						{<p className="m-card-layer__description">{bodyRegions.join(', ')}</p>}
					</div>
					<div className="m-card-layer__content">
						{!contentVideo && (
							<ContentImageSlider
								id={this.state.currentExerciseId}
								images={images}
								seconds={getSeconds()}
							/>
						)}
						{contentVideo && (
							<ContentVideo
								caption={contentVideo.caption}
								video={contentVideo.video}
								seconds={getSeconds()}
							/>
						)}
					</div>
					<ButtonBar
						handleButtonClicks={this.onSwitchLayer}
						buttonsLayout={cardButtons.buttonsLayout}
						buttons={cardButtons.buttons}
					/>
				</div>
			</div>
		);
	}
}

export default CardLayer;
